/**
 * Source of truth for project-file extraction structured outputs.
 *
 * Every segment extraction payload is defined as a Zod schema and the
 * JSON Schema handed to OpenAI's Responses `json_schema` format is derived
 * via {@link z.toJSONSchema}. The same Zod schema is used on the consumer
 * side to parse and narrow the model response — no hand-drift between the
 * contract we ask the model to honor and the contract we validate against.
 *
 * All fields are required (OpenAI strict mode forbids optional keys);
 * use `z.nullable(...)` to allow the model to emit null when a value is
 * absent instead of `.optional()`.
 */

import { z } from 'zod';
import type { ProjectFileClassificationType } from '../models/project-file-batch.model';
import { buildingPartRequirementsAiArraySchema } from '../models/building-part-requirement.model';
import { northMethodSchema } from '../models/project-file-drawing.model';
import {
  ROOF_FORM_TYPES,
  READ_FROMS,
  READ_SOURCES,
  WINDOW_RATIO_DETERMINED_BY_DESCRIPTION,
  WINDOW_RATIO_DETERMINED_BY,
  WINDOW_RATIO_VALUE_DESCRIPTION,
  PRINTED_LOCATION_DESCRIPTION,
  EXTRACTED_PROGRAM_LABEL_DESCRIPTION,
  EXTRACTED_PROGRAM_SHARE_SEGMENT_DESCRIPTION,
  EXTRACTED_PROGRAM_TYPE_DESCRIPTION,
  extractedAreaKindSchema,
  extractedDesignFactsAiSchema,
  extractedProgramTypeSchema,
  extractedResidentialAiSchema,
  extractedStairwellsAiSchema,
  extractedVolumeAreasAiFields,
  type RoofFormType,
  type Read,
} from '../models/extracted-design-facts.model';

const nullableStr = (description: string) =>
  z.nullable(z.string()).describe(description);

const nullableNum = (description: string) =>
  z.nullable(z.number()).describe(description);

const roofReadAi = <V>(value: z.ZodType<V>, valueDescription: string) =>
  z
    .object({
      value: value.nullable().describe(valueDescription),
      source: z
        .enum(READ_SOURCES)
        .nullable()
        .describe(
          'text = words or a printed number on this segment. geometry = the drawn section or elevation. Null when value is null.',
        ),
      from: z
        .enum(READ_FROMS)
        .nullable()
        .describe(
          'direct = the segment states it. estimate = guessed. extrapolated = calculated from other marks. Null when value is null.',
        ),
    })
    .strict();

/** OpenAI strict roof object. Every key required; unused reads are null. */
export const blueprintRoofFormAiSchema = z
  .object({
    type: roofReadAi(
      z.enum(ROOF_FORM_TYPES),
      [
        'Roof form the segment shows: flat, sadel, pulpet, or mansard.',
        'In a section or an elevation, read it from the drawn outline of the top roof: two slopes meeting at a ridge is sadel, one slope across the whole roof is pulpet, two slopes on each side with the lower one steeper is mansard, a level roof is flat (a parapet or a raised edge does not change that).',
        'A slope angle drawn on the roof outline belongs to a sloped form, so the outline shows the form: read it.',
        'Null only when the segment draws no roof outline and names no form. A slope adjective in prose alone is not a form.',
      ].join(' '),
    ),
    pitch_deg: roofReadAi(
      z.number(),
      'Slope in degrees when the segment states one. Null for flat, and null when no number is read. Not a source note.',
    ),
  })
  .strict()
  .describe(
    'Roof form and pitch read from this segment. Null when the drawing does not show the roof.',
  );

const completeRead = <V>(read: {
  value: V | null;
  source: Read<V>['source'] | null;
  from: Read<V>['from'] | null;
}): Read<V> | null => {
  if (read.value === null || read.source === null || read.from === null) {
    return null;
  }
  return { value: read.value, source: read.source, from: read.from };
};

/**
 * Structural narrow of the model object. A prose string is not a roof form.
 */
export const parseBlueprintRoofForm = (
  raw: unknown,
): {
  type: Read<RoofFormType> | null;
  pitchDeg: Read<number> | null;
} | null => {
  const parsed = blueprintRoofFormAiSchema.safeParse(raw);
  if (!parsed.success) return null;
  const type = completeRead(parsed.data.type);
  const pitchDeg =
    type?.value === 'flat' ? null : completeRead(parsed.data.pitch_deg);
  if (type === null && pitchDeg === null) return null;
  return { type, pitchDeg };
};

/** Strip `$schema` meta — OpenAI structured outputs don't need it. */
const toOpenAiStrictJsonSchema = (
  schema: z.ZodType,
): Record<string, unknown> => {
  const { $schema: _ignored, ...rest } = z.toJSONSchema(schema) as Record<
    string,
    unknown
  > & { $schema?: string };
  return rest;
};

// ---------------------------------------------------------------------------
// Blueprint extraction
// ---------------------------------------------------------------------------

const point2ItemSchema = z.object({
  x: z.number().describe('X in meters.'),
  y: z.number().describe('Y in meters.'),
});

/** One corner of a footprint polygon, as fractions of the page. */
const footprintPointSchema = z.object({
  x: z
    .number()
    .describe('Fraction of the page width from the left edge, 0 to 1.'),
  y: z
    .number()
    .describe('Fraction of the page height from the top edge, 0 to 1.'),
});

export const footprintPolygonAiSchema = z.object({
  cutout: z
    .boolean()
    .describe(
      'True for an inner courtyard (a hole in the building outline). False for every other polygon.',
    ),
  points: z
    .array(footprintPointSchema)
    .describe(
      'Corner points of one closed polygon, in order around it, at least three. The first point is not repeated at the end.',
    ),
});

/** One plan on a page and the building outline drawn on it. */
export const storeyFootprintAiSchema = z.object({
  page_number: z
    .number()
    .int()
    .describe('1-based page of the attached file this plan is on.'),
  plan_label: nullableStr(
    'Name this plan prints for its level (Plan 16, Entréplan, Normalplan). Null when it prints none.',
  ),
  polygons: z
    .array(footprintPolygonAiSchema)
    .describe(
      "The building's outline on this plan, one polygon per separate volume.",
    ),
});

export type StoreyFootprintAi = z.infer<typeof storeyFootprintAiSchema>;

/** Root of the per-plan footprint call: the outline on one cropped plan. */
export const drawingFootprintAiSchema = z.object({
  polygons: z
    .array(
      footprintPolygonAiSchema.extend({
        buildings: z
          .array(z.string())
          .describe(
            'Ids from the list in the prompt of every building or part this polygon outlines: several when one outline spans parts joined wall to wall. Empty when the sheet shows one building, or when no name printed on this plan tells which it is.',
          ),
        printed_name: nullableStr(
          'The name this plan prints inside or next to the outline for its building or part (for example "Kv 4" or "Hus B"), as printed. Null when the plan prints no name for it.',
        ),
        site_number: z
          .number()
          .int()
          .positive()
          .nullable()
          .describe(
            'Your own number for the building this polygon belongs to, given the same way on every plan of the site, whether or not names are printed: the buildings this plan shows are numbered 1, 2, 3 … in reading order of where they stand, the top row first and left to right within a row. Every polygon of one building has its number. Null when the plan shows one building.',
          ),
        printed_areas_m2: z
          .array(z.number().positive())
          .describe(
            'Every area in m² printed inside this polygon on the plan: one number per apartment, room or premises label, as printed. Not totals printed outside the polygon, not dimensions. Empty when none is printed inside it.',
          ),
        fixed_features: z
          .array(
            z.object({
              kind: z
                .enum(['stair', 'lift', 'shaft', 'grid'])
                .describe(
                  'stair: an enclosed stairwell (trapphus). lift: a lift shaft (hiss). shaft: a vertical installation shaft (schakt). grid: where two named structural grid lines (stomlinjer) cross.',
                ),
              label: nullableStr(
                'What the plan prints for it: the two grid line names as "A/3", or the name of a stair or lift (TR1, Hiss A). Null when it prints none.',
              ),
              outline: z
                .array(
                  z.object({
                    x: z
                      .number()
                      .describe(
                        'Fraction of the image width from the left edge, 0 to 1.',
                      ),
                    y: z
                      .number()
                      .describe(
                        'Fraction of the image height from the top edge, 0 to 1.',
                      ),
                  }),
                )
                .describe(
                  'For a stair, lift or shaft: the inner face of the walls around it, one point per corner in order. For a grid crossing: one point where the lines cross.',
                ),
            }),
          )
          .describe(
            'Features inside this polygon that stand in the same place on every storey of the building, so the storeys can be set over one another. Empty for a cutout and when the plan shows none.',
          ),
        outline_below: z
          .array(
            z.object({
              x: z
                .number()
                .describe(
                  'Fraction of the image width from the left edge, 0 to 1.',
                ),
              y: z
                .number()
                .describe(
                  'Fraction of the image height from the top edge, 0 to 1.',
                ),
            }),
          )
          .describe(
            "Corner points of the storey below this polygon's floor plate, where this plan draws it (often a dashed or thin line), in order around it. Empty when the plan does not draw it, and for a cutout.",
          ),
        points: z
          .array(
            z.object({
              x: z
                .number()
                .describe(
                  'Fraction of the image width from the left edge, 0 to 1.',
                ),
              y: z
                .number()
                .describe(
                  'Fraction of the image height from the top edge, 0 to 1.',
                ),
            }),
          )
          .describe(
            'Corner points of one closed polygon, in order around it, at least three. The first point is not repeated at the end.',
          ),
      }),
    )
    .describe(
      "The building's outline on this plan, one polygon per separate volume. Empty only when the image shows no building plan.",
    ),
});

export type DrawingFootprintAi = z.infer<typeof drawingFootprintAiSchema>;

/** OpenAI structured output name for the per-plan footprint call. */
export const PROJECT_FILE_DRAWING_FOOTPRINT_SCHEMA_NAME =
  'project_file_drawing_footprint' as const;

export const buildDrawingFootprintJsonSchema = (): Record<string, unknown> =>
  toOpenAiStrictJsonSchema(drawingFootprintAiSchema);

const dimensionsPartSchema = z.object({
  name: nullableStr(
    'Name printed on the drawing or title. Null when the document does not name this piece.',
  ),
});

const dimensionsSchema = z.object({
  parts: z
    .nullable(z.array(dimensionsPartSchema))
    .describe(
      'Only when the document itself names a piece of that building. A name printed on a drawing or a title is enough. Null when the document names none. Not a mass, a wing, a compass direction, or a storey that covers only part of the footprint.',
    ),
});

/** One count for every storey index the steps write, so they share one key. */
export const STOREY_INDEX_FROM_ENTRANCE =
  'The storey counted from the entrance storey: 0 is the entrance storey, -1 the storey below it, 1 the storey above it.';

/** A level inside a storey is not a storey row, so it cannot shift the stack. */
export const LEVELS_WITHIN_STOREY_DESCRIPTION =
  'Printed levels in metres inside this storey that are not a storey of their own: a stair landing, a half level, a part of the floor set on another level, an entrance level set into the slope. Such a level stays on the storey it belongs to and gets no row, index or height of its own. Empty when there is none.';

const storeyIndexField = z
  .number()
  .int()
  .nullable()
  .describe(
    `The floor this row sits on. ${STOREY_INDEX_FROM_ENTRANCE} The same floor uses the same index on its height row and its program rows. A printed plan number is not the index. The printed name stays in storey_label. Null when the sheet does not place the row.`,
  );

const storeyHeightRowSchema = z.object({
  part_name: nullableStr(
    'Name printed on the drawing or title for the piece this row belongs to. Null when this drawing does not name a piece.',
  ),
  storey_index: storeyIndexField,
  storey_label: nullableStr(
    'Plan number this drawing shows for the floor, written `Plan N`. When this drawing shows the stack and a numbered floor, the entrance row carries the plan number those determine, even when the entrance number is not printed. Otherwise the name this drawing prints. Never a name this drawing does not print for a floor it does not number. Null when this drawing shows neither. One source for that index.',
  ),
  inner_height_m: nullableNum(
    "Room height in metres printed for this storey, including the uppermost storey and a technical storey. A use label on the storey does not make this null. Null only when this storey prints no room height. Not a structural floor height. When the drawing shows more than one section, this is the room height drawn inside this section for this storey, with this section's own structural height. A number on the same horizontal line as another section's roof or title still belongs to the section it is drawn in.",
  ),
  floor_to_floor_height_m: nullableNum(
    'Structural floor height in metres printed for this storey (floor-to-floor, bjälklagshöjd, or våningshöjd), including the uppermost dimensioned storey. A title storey count does not drop a height this storey prints. Null only when this storey prints none. Not rumshöjd.',
  ),
  floor_level_m: nullableNum(
    'Level in metres printed at the finished floor of this storey on this drawing (FG, färdigt golv, a +NN.NN level mark), as printed. Null when this drawing prints no level for that floor.',
  ),
  levels_within_m: z
    .array(z.number())
    .describe(LEVELS_WITHIN_STOREY_DESCRIPTION),
  area_m2: nullableNum(
    'Floor area in m² of this storey, of this piece when the row names one: the total the plan prints for the floor, else the sum of the named areas printed on that floor of the piece (apartments, lokaler, stairwells, technical rooms). Null when this drawing prints no area on that floor.',
  ),
});

const blueprintProgramRowScopeSchema = z.enum([
  'zone',
  'storey_total',
  'building_total',
]);

const blueprintProgramSchema = z.object({
  part_name: nullableStr(
    'Name printed on the drawing or title for the piece this area sits in, when one plan sheet shows several named pieces and this area belongs to one of them. Same name as on that piece storey rows. Null when this drawing does not name a piece for the area.',
  ),
  row_scope: z
    .nullable(blueprintProgramRowScopeSchema)
    .describe(
      'zone when the sheet prints this area as one zone line on the floor. storey_total when it is the sum for the whole floor. building_total when it is a sum for the whole building. Null when the sheet does not say.',
    ),
  storey_index: storeyIndexField,
  storey_label: nullableStr(
    "Printed floor this area sits on (Plan N, entréplan). The area's own name is `label`. A word that names the room is not a floor, and it is not a storey the sheet does not show. Null when the sheet does not name the floor.",
  ),
  type: extractedProgramTypeSchema.describe(EXTRACTED_PROGRAM_TYPE_DESCRIPTION),
  area_m2: nullableNum(
    'Printed area of this program on that storey, in m², including a partial area. Null when no area is printed.',
  ),
  share: nullableNum(EXTRACTED_PROGRAM_SHARE_SEGMENT_DESCRIPTION),
  label: nullableStr(EXTRACTED_PROGRAM_LABEL_DESCRIPTION),
  apartment_count: nullableNum(
    'Number of apartments this residential row covers on its storey: 1 for one apartment line, the number of apartments when the row is a printed or drawn group. Null on a row that is not residential, and null when the sheet does not show how many apartments the row covers.',
  ),
});

export const singleBuildingBlueprintItemSchema = z.object({
  building_instance_key: nullableStr(
    'The classifier building id for this row when the segment lists one. Copy it. When the list is empty, the name this drawing prints for the building it is of. A token that only identifies the sheet is not a key. Do not mint a key for a neighbor, a background building, a cropped fragment, or a plot or area name.',
  ),
  building_label: nullableStr(
    'Stamp or table name for this building when shown.',
  ),
  sheet_scope_note: nullableStr(
    'What this drawing shows (e.g. plan 3, fasad öst, sektion A–A).',
  ),
  accurate_description: z
    .string()
    .describe(
      [
        'Required. Prose describing the BUILDING VOLUME(S) in this segment for a downstream design AI.',
        'INCLUDE — and be quantitative when the drawing shows it:',
        '- each distinct mass and how it sits relative to the others;',
        '- total height per mass (meters) and storey count, plus typical storey height;',
        '- relationships between masses: podium + tower, garage base + residential block, connecting bridges, shared base slab, off-axis stacking (e.g. tower sits at east end of garage podium), step-backs, set-backs, overhangs;',
        '- approximate footprint of any podium/garage relative to the building(s) sitting on it;',
        '- roof form (flat, sadel, pulpet, mansard) and parapet hints when readable.',
        'Do not describe an invented mass, wing, compass direction, or a storey that covers only part of the footprint as a part.',
        'EXCLUDE — irrelevant for building volume modelling:',
        '- absolute elevations / height above sea level, +RH2000 levels;',
        '- apartment unit sizes, room programmes, lägenhetsfördelning, BOA/LOA breakdowns;',
        '- street addresses, fastighetsbeteckningar, property boundaries;',
        '- materials, finishes, colors unless they imply massing;',
        '- regulatory references (BBR, detaljplan paragraphs).',
        'The structured numeric fields below are what nodon can directly consume — describing volume facts here that the schema cannot express is the whole point. Swedish welcome when sources are Swedish.',
      ].join('\n'),
    ),
  buildingHeightMeters: nullableNum(
    'Total building height in meters (roof/parapet if present); null if unknown.',
  ),
  points2D: z
    .nullable(z.array(point2ItemSchema))
    .describe(
      'Ground floor footprint polygon in meters (local 2D coords); null if unreadable.',
    ),
  storey_footprints: z
    .array(storeyFootprintAiSchema)
    .describe(
      'Plan views only. One row per plan on this sheet that shows a level of this building: the outline of the building on that plan. Coordinates are fractions of the page. Empty when the sheet shows no plan, or only a section, an elevation or a site plan.',
    ),
  storeyCount: nullableNum('Storey count if clearly indicated; else null.'),
  storey_heights: z
    .array(storeyHeightRowSchema)
    .describe(
      "The storeys this drawing shows, one row per floor it shows, and one row per part when the drawing names parts. A whole stack includes each floor from the entrance through the top floor on this drawing, including the uppermost storey it dimensions when a title states a lower count. A room height and a structural floor height drawn inside one section sit on that section's part and that storey. Do not take a room height from a neighbouring section. One source. This step does not own the storey list. Empty when the drawing shows no floors.",
    ),
  dimensions: dimensionsSchema,
  floors_count: nullableNum('Interpreted storey count.'),
  floors_interpretation_note: nullableStr(
    'How storey count was read. A printed plan number may be the number of storeys, or it may not; say which reading you used. Null if n/a.',
  ),
  area_per_floor_m2: nullableNum('Typical storey GFA.'),
  total_area_m2: nullableNum('Whole-building approximate area.'),
  programs: z
    .array(blueprintProgramSchema)
    .describe(
      'Program areas this sheet prints on a storey, including a partial area. Each unmapped program is its own row. Its stated area stays with the printed word. Empty when none are printed.',
    ),
  slab_thickness_mm: nullableNum(
    'Slab thickness in millimetres when the drawing states one number. Null when it does not. Not a typcode and not a part name.',
  ),
  window_ratio: nullableNum(WINDOW_RATIO_VALUE_DESCRIPTION),
  window_ratio_determined_by: z
    .enum(WINDOW_RATIO_DETERMINED_BY)
    .nullable()
    .describe(WINDOW_RATIO_DETERMINED_BY_DESCRIPTION),
  stairwell_tags: z
    .array(z.string())
    .describe(
      'The stairwell tags or identifiers printed on this plan, one entry per stairwell, each written as printed (for example the text of a TH tag). Counted whether or not the sheet shows the whole building: list the tags that are printed on it. Empty when the plan prints no stairwell tags.',
    ),
  stairwell_count: nullableNum(
    'Number of separate stairwells (vertical cores) this plan shows, counting each core once. A core drawn as several zone lines is still one. Count what is drawn, also when the sheet is a crop or shows several building parts; a sheet that shows only a part of the building gives the count of that part. Used when the plan prints no stairwell tags. Null when the sheet is not a plan or shows no stairwell.',
  ),
  bathroom_area_per_apartment_m2: nullableNum(
    'Bathroom area per apartment in m² when this plan shows apartment plans with bathrooms. The printed area, or the product of the bathroom sides when those are printed or dimensioned on the drawing, or the sides read off the drawing scale. Null when no bathroom is shown or it cannot be sized.',
  ),
  bathroom_area_determined_by: z
    .enum(WINDOW_RATIO_DETERMINED_BY)
    .nullable()
    .describe(
      'How bathroom_area_per_apartment_m2 was determined. read: a printed area. calculated: the product of printed or dimensioned sides. estimated: sides read off the drawing scale. Null when bathroom_area_per_apartment_m2 is null.',
    ),
  roof_form: blueprintRoofFormAiSchema,
});
export type SingleBuildingBlueprintItem = z.infer<
  typeof singleBuildingBlueprintItemSchema
>;

export const blueprintExtractionRootSchema = z.object({
  buildings: z
    .array(singleBuildingBlueprintItemSchema)
    .describe(
      "When the classifier segment context lists building ids, one row per id listed there — not for volumes the classifier marked as accessory_volumes_on_main_block on the main block. When that list is empty, one row per building this drawing is of, with the storey rows this drawing shows. An empty id list is not an empty drawing. Do not add a row for context around those buildings: an existing neighbor, a background building, or a fragment cut by the sheet edge. A plot name or an area name that only locates a listed building is that building's label, not another row. Copy a listed classifier id into `building_instance_key`. When the list is empty, put the name this drawing prints for that building there. A token that only identifies the sheet is not a key. Do not mint a key for a neighbor, a background building, a cropped fragment, or a plot or area name. A named piece of a listed building stays on that building's parts. Return an empty array when the excerpt is unreadable or the drawing shows no building — do NOT invent rows.",
    ),
  building_part_requirements: buildingPartRequirementsAiArraySchema,
});
export type BlueprintExtractionRoot = z.infer<
  typeof blueprintExtractionRootSchema
>;

/** OpenAI structured output name for blueprint segment extraction. */
export const PROJECT_FILE_BLUEPRINT_EXTRACTION_SCHEMA_NAME =
  'project_file_blueprint_extraction' as const;

/** Strict JSON Schema for blueprint extraction structured output. */
export const buildBlueprintExtractionRootJsonSchema = (): Record<
  string,
  unknown
> => toOpenAiStrictJsonSchema(blueprintExtractionRootSchema);

// ---------------------------------------------------------------------------
// Non-blueprint extraction (one schema per classification type)
// ---------------------------------------------------------------------------

const confidenceField = z
  .number()
  .describe('0–1 certainty of this row; lower when guessing or abstaining.');

const unknownExtractionSchema = z.object({
  abstain: z.boolean().describe('True when unreadable.'),
  confidence: confidenceField,
  why: nullableStr('Brief reason when abstaining.'),
});

const buildingRenderingExtractionSchema = z.object({
  observed_massing: nullableStr(
    'Overall silhouette, wing layout, roof form and massing cues we can model (not marketing copy).',
  ),
  facade_or_height_notes: nullableStr(
    'Readable storey rhythm, parapet/height hints, setbacks from ground when visible.',
  ),
  window_ratio: nullableNum(WINDOW_RATIO_VALUE_DESCRIPTION),
  window_ratio_determined_by: z
    .enum(WINDOW_RATIO_DETERMINED_BY)
    .nullable()
    .describe(WINDOW_RATIO_DETERMINED_BY_DESCRIPTION),
  accurate_description: z
    .string()
    .describe(
      [
        'Required. Prose describing the BUILDING VOLUME(S) visible in the rendering for a downstream design AI.',
        'INCLUDE: approximate length/depth/height per mass when inferable, storey count, relationships between masses (podium + tower, garage base + residential block, shared base slab, off-axis stacking), roof form.',
        'EXCLUDE: apartment/room programmes, materials/finishes/colors unless they imply massing, marketing copy, addresses, regulatory references.',
        'Stay grounded in the rendering; cautious language fine when the angle hides extents. Swedish welcome when sources are Swedish.',
      ].join('\n'),
    ),
  confidence: confidenceField,
});

const projectDescriptionExtractionSchema = z.object({
  project_type: nullableStr('Inferred programme / building-type label.'),
  location: nullableStr(PRINTED_LOCATION_DESCRIPTION),
  building_description: nullableStr('Short prose summary.'),
  confidence: confidenceField,
});

const projectBriefExtractionSchema = z.object({
  building_part_requirements: buildingPartRequirementsAiArraySchema,
  design_facts: extractedDesignFactsAiSchema.describe(
    'Resolved design facts grounded in this brief. Null leaves when the brief does not state them. Do not invent.',
  ),
  confidence: confidenceField,
});

const areaScheduleRowScopeSchema = z.enum([
  'per_storey',
  'building_total',
  'typical_per_storey',
  'typical_sum',
]);

const areaScheduleZoneSchema = z.object({
  zone: nullableStr(
    'Zone or room label as printed (apartment type like 3 RoK, lokal, BTA, summa).',
  ),
  floor: nullableStr(
    'Storey this line belongs to, as printed (entréplan, plan 3). One plan per value when the table lists plans separately. Null only when the line is a whole-building total.',
  ),
  building_instance_keys: z
    .array(z.string())
    .describe(
      'The classifier building ids this row is printed for, only ids from the classifier segment context. A column, heading or row label that names one listed building, also by an abbreviation, gives that one id, even when the list also has an id for the whole site or block that contains it. A row printed for several listed buildings together (a pair, a total, a per-plan sum over every building) lists every id it covers. Empty when no listed id names the building the row is for.',
    ),
  part_name: nullableStr(
    'When the table names a piece of one building on this row (Del A, a wing), copy that printed name. A whole building is never a part: it goes in `building_instance_keys`. Null when the row is not scoped to one named piece.',
  ),
  row_scope: z
    .nullable(areaScheduleRowScopeSchema)
    .describe(
      'per_storey when the row is for one numbered plan or named entrance/attic storey. building_total for a whole-building sum. For an area printed for a normalplan/zon without its own plan number (not a storey row): typical_per_storey when it is the area of each storey it stands for, typical_sum when the table prints it as the total of only those storeys together. A total that also counts a storey with its own plan (normalplan + entréplan) is building_total when it covers every storey of the building.',
    ),
  plan_number: nullableNum(
    'Plan number this row belongs to when row_scope is per_storey. Null for building totals and typical rows.',
  ),
  area_m2: nullableNum('Area in m² as printed.'),
  share: nullableNum(EXTRACTED_PROGRAM_SHARE_SEGMENT_DESCRIPTION),
  activity_type: z
    .nullable(extractedProgramTypeSchema)
    .describe(
      `${EXTRACTED_PROGRAM_TYPE_DESCRIPTION} Null for a storey floor-total row and for whole-building totals, except the use of a BTA printed for one use (BTA bostäder is residential). Zone keeps the printed word when type is other.`,
    ),
  area_kind: z
    .nullable(extractedAreaKindSchema)
    .describe(
      'bta for this storey’s floor total (våningsyta / plan BTA / summa for that floor) or the whole building’s BTA — activity_type null. The BTA of one use (BTA bostäder, BTA lokaler) is bta with that use’s activity_type. ljus_bta, mork_bta (mörk BTA), boa, loa, bya, or bra when the table says so; unknown for apartment-type (N RoK) or room lines; null if unreadable. Never use bta on an RoK row.',
    ),
});

const areaScheduleExtractionSchema = z.object({
  areas_by_zone: z.nullable(
    z
      .array(areaScheduleZoneSchema)
      .describe('One row per printed area line; null if unreadable.'),
  ),
  total_area_m2: nullableNum(
    'Whole-building BTA when the whole table is one building and gives a total; null otherwise. A total over several buildings stays on its `areas_by_zone` row.',
  ),
  building_instance_key: nullableStr(
    'Copy the classifier building id only when the whole table is about that one building; null otherwise.',
  ),
  building_name: nullableStr('Building name as printed; null if unknown.'),
  storey_count: nullableNum(
    'Storey count if the schedule states it; null otherwise.',
  ),
  ...extractedVolumeAreasAiFields,
  residential: extractedResidentialAiSchema,
  stairwells: extractedStairwellsAiSchema,
  confidence: confidenceField,
});

const bomExtractionSchema = z.object({
  representative_lines: z.nullable(
    z.array(
      z.object({
        article: nullableStr('Article or product id.'),
        qty: nullableNum('Quantity.'),
        unit: nullableStr('Unit of measure.'),
      }),
    ),
  ),
  confidence: confidenceField,
});

const costEstimateExtractionSchema = z.object({
  totals_or_lines: z.nullable(
    z.array(
      z.object({
        label: nullableStr('Line description.'),
        amount: nullableNum('Amount.'),
        currency: nullableStr('Currency code if known.'),
      }),
    ),
  ),
  confidence: confidenceField,
});

const technicalSpecExtractionSchema = z.object({
  building_part_requirements: buildingPartRequirementsAiArraySchema,
  design_facts: extractedDesignFactsAiSchema.describe(
    'Design facts grounded in this FFU / teknisk beskrivning. Null leaves when not stated. Do not invent a typical span or a typical size.',
  ),
  confidence: confidenceField,
});

const titleBlockExtractionSchema = z.object({
  drawing_id: nullableStr('Drawing number / id.'),
  revision: nullableStr('Revision id.'),
  project_title: nullableStr(
    'Project name as printed on the stamp, including a place printed with that name. Do not shorten it to the building name.',
  ),
  confidence: confidenceField,
});

/** Per classification type (excluding blueprint): Zod schema for response. */
export const NON_BLUEPRINT_EXTRACTION_SCHEMAS = {
  building_rendering: buildingRenderingExtractionSchema,
  project_description: projectDescriptionExtractionSchema,
  project_brief: projectBriefExtractionSchema,
  area_schedule: areaScheduleExtractionSchema,
  bill_of_materials: bomExtractionSchema,
  cost_estimate: costEstimateExtractionSchema,
  technical_spec: technicalSpecExtractionSchema,
  title_block: titleBlockExtractionSchema,
  unknown: unknownExtractionSchema,
} as const satisfies Record<
  Exclude<ProjectFileClassificationType, 'blueprint'>,
  z.ZodType
>;

/** Structured output wrapper name per segment type (`project_file_extract_*`). */
export const PROJECT_FILE_NON_BLUEPRINT_EXTRACT_SCHEMA_NAMES = {
  building_rendering: 'project_file_extract_building_rendering',
  project_description: 'project_file_extract_project_description',
  project_brief: 'project_file_extract_project_brief',
  area_schedule: 'project_file_extract_area_schedule',
  bill_of_materials: 'project_file_extract_bill_of_materials',
  cost_estimate: 'project_file_extract_cost_estimate',
  technical_spec: 'project_file_extract_technical_spec',
  title_block: 'project_file_extract_title_block',
  unknown: 'project_file_extract_unknown',
} as const satisfies Record<
  Exclude<ProjectFileClassificationType, 'blueprint'>,
  string
>;

/** Name + derived JSON Schema for non-blueprint extractor OpenAI Responses `json_schema` block. */
export const getNonBlueprintSegmentStructuredOutput = (
  segmentType: Exclude<ProjectFileClassificationType, 'blueprint'>,
): { name: string; schema: Record<string, unknown> } => ({
  name: PROJECT_FILE_NON_BLUEPRINT_EXTRACT_SCHEMA_NAMES[segmentType],
  schema: toOpenAiStrictJsonSchema(
    NON_BLUEPRINT_EXTRACTION_SCHEMAS[segmentType],
  ),
});

// ---------------------------------------------------------------------------
// Site model: every building and storey read from all plan pages at once
// ---------------------------------------------------------------------------

const siteModelPolygonAiSchema = z.object({
  cutout: z
    .boolean()
    .describe(
      'True for an inner courtyard (a hole in the floor plate). False for every other polygon.',
    ),
  points: z
    .array(
      z.object({
        x: z.number().describe('Metres east of the common origin.'),
        y: z.number().describe('Metres south of the common origin.'),
      }),
    )
    .describe(
      'Corner points of one closed polygon in the common frame, in order around it, at least three. The first point is not repeated at the end.',
    ),
  page_points: z
    .array(
      z.object({
        x: z
          .number()
          .describe('Fraction of the image width from its left edge, 0 to 1.'),
        y: z
          .number()
          .describe('Fraction of the image height from its top edge, 0 to 1.'),
      }),
    )
    .describe(
      'On the bottom storey of a building: the same corners as `points`, in the same order, where they sit on the `source_image` image. Empty on every other storey.',
    ),
  site_points: z
    .array(
      z.object({
        x: z
          .number()
          .describe('Fraction of the image width from its left edge, 0 to 1.'),
        y: z
          .number()
          .describe('Fraction of the image height from its top edge, 0 to 1.'),
      }),
    )
    .describe(
      'On the bottom storey of a building the `site_plan_image` shows: the same corners as `points`, in the same order, where they sit on that image. Empty on every other storey and when `site_plan_image` is null.',
    ),
});

const siteModelStoreyAiSchema = z.object({
  index: z.number().int().describe(STOREY_INDEX_FROM_ENTRANCE),
  name: nullableStr(
    'The name the documents print for this storey (Plan 14, Entréplan, Våning 2). Null when they print none.',
  ),
  plan_numbers: z
    .array(z.number().int())
    .describe(
      'Plan numbers printed for this storey (Plan 14 gives 14). Empty when none is printed.',
    ),
  source_image: z
    .string()
    .describe('The image (P1, P2 …) whose drawing the outline is read from.'),
  source_north_deg: z
    .number()
    .describe(
      'Degrees clockwise from straight up on `source_image` to north on its drawing: how the outline was turned into the frame.',
    ),
  source_north_method: northMethodSchema
    .exclude(['none'])
    .describe(
      "How `source_north_deg` is known. north_arrow = the drawing's own north arrow or compass rose. coordinate_grid = map coordinates printed on it. matched_drawing = the drawing shows no north of its own; it is turned until the building matches another drawing of it whose north is read, named in `source_north_matched_image`. assumed = nothing gives north, and page up is taken as north.",
    ),
  source_north_matched_image: nullableStr(
    'When `source_north_method` is matched_drawing: the image (P1, P2 …) whose drawing north was taken from. Null otherwise.',
  ),
  polygons: z
    .array(siteModelPolygonAiSchema)
    .describe(
      "The storey's floor plate: one polygon per volume standing apart on this storey, plus a cutout polygon per inner courtyard.",
    ),
});

/** Root of the site model call: the buildings of the project as they stand. */
export const siteModelAiSchema = z.object({
  site_plan_image: nullableStr(
    'The image (P1, P2 …) of the site plan that sets where the buildings stand relative to each other. Null when no site plan shows several of them together.',
  ),
  buildings: z
    .array(
      z.object({
        ids: z
          .array(z.string())
          .describe(
            'Ids from the list in the prompt of every building or part this building is. Several when parts on the list stand joined in one floor plate. Empty when it is on no line of the list.',
          ),
        name: nullableStr(
          'The name the documents print for this building, as printed. Null when they print none.',
        ),
        storeys: z
          .array(siteModelStoreyAiSchema)
          .describe('Every storey of the building, bottom to top.'),
      }),
    )
    .describe(
      'Every building of the project. A building is one floor plate standing on its own on at least one storey.',
    ),
});

export type SiteModelAi = z.infer<typeof siteModelAiSchema>;

/** OpenAI structured output name for the site model call. */
export const PROJECT_FILE_SITE_MODEL_SCHEMA_NAME =
  'project_file_site_model' as const;

export const buildSiteModelJsonSchema = (): Record<string, unknown> =>
  toOpenAiStrictJsonSchema(siteModelAiSchema);
